import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Loader2, AlertTriangle } from 'lucide-react';

/**
 * Handles the redirect back from Emergent's Google Auth.
 * Lands here as: /auth/google/callback#session_id=XYZ
 * Exchanges session_id for a Numuv JWT, then navigates to the role-specific dashboard.
 */
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const setAuthState = useAuthStore.setState;
  const [error, setError] = useState('');
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');
    const preferredRole = localStorage.getItem('google_auth_role') || 'applicant';

    if (!sessionId) {
      setError('Missing Google session. Please try signing in again.');
      return;
    }

    (async () => {
      try {
        const resp = await authAPI.googleSession(sessionId, preferredRole);
        localStorage.setItem('auth_token', resp.access_token);
        localStorage.removeItem('google_auth_role');
        setAuthState({ token: resp.access_token, user: resp.user, isAuthenticated: true, isLoading: false });
        const role = resp.user?.role || preferredRole;
        navigate(`/${role}`, { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6" data-testid="google-auth-callback">
      <div className="text-center max-w-sm">
        {error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-5">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mb-2 mx-auto" />
            <p className="text-red-700 dark:text-red-300 text-sm font-medium" data-testid="google-callback-error">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-4 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium"
              data-testid="google-callback-back-btn"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400 animate-spin" />
            <p className="text-slate-500 dark:text-zinc-400 text-sm">Finishing sign-in with Google…</p>
          </div>
        )}
      </div>
    </div>
  );
}
